// Utils
import { fetchText } from 'utils/fetch';
import { stringToNode } from 'utils/node';
import waitToLoad from 'utils/waitToLoad';

// Constants
export const ALGOLIA_TRENDING = 'trending-items';
export const ALGOLIA_RELATED = 'related-products';
export const ALGOLIA_BOUGHT_TOGETHER = 'bought-together';

const createAlgoliaRecommendationsEngine = ({
  compilationTemplate,
  retrievableAttributes,
  itemQuery,
  limit,
}) => {
  const {
    indices: { productsIndex },
    appId,
    apiKey,
  } = window.theme.algoliaSettings;

  let algoliaClient = null;
  let algoliaLoaded = false;

  const load = async () => {
    await waitToLoad('AlgoliaSearch');

    const { algoliasearch } = window;
    algoliaLoaded = true;
    algoliaClient = algoliasearch(appId, apiKey);
  };

  const fetchRecommendationsHTML = async (hits) => {
    const handlesToFetch = hits.map((hit) => hit.handle);

    try {
      const htmlString = await fetchText(
        `/search?view=${compilationTemplate}&q=${handlesToFetch.join(',')}`,
        {
          method: 'GET',
        },
      );
      const [elements] = stringToNode(htmlString, [itemQuery]);

      return Promise.resolve(elements);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const query = async (recModel, items = []) => {
    if (!algoliaLoaded) await load();

    const queries =
      recModel === ALGOLIA_TRENDING
        ? [
            {
              model: recModel,
              indexName: productsIndex,
              threshold: 0,
              maxRecommendations: 20,
              queryParameters: {
                attributesToSnippet: [],
                attributesToHighlight: [],
                attributesToRetrieve: retrievableAttributes,
              },
            },
          ]
        : items.map((item) => ({
            model: recModel,
            indexName: productsIndex,
            maxRecommendations: 10,
            threshold: 0,
            objectID: `${item.variant_id}`,
            queryParameters: {
              attributesToSnippet: [],
              attributesToHighlight: [],
              attributesToRetrieve: retrievableAttributes,
            },
          }));

    try {
      const { results = [] } =
        queries.length > 0 ? await algoliaClient.getRecommendations(queries) : { results: [] };

      const cartProductIds = items.map((item) => item.product_id);
      const allHits = results.reduce((acc, result) => [...acc, ...result.hits], []);

      // Combine the hits from all results (check with client if they want this)
      const hits =
        items.length > 1
          ? allHits
              .filter((hit) => (items.length > 1 ? !cartProductIds.includes(hit.id) : true))
              .filter(
                (hit, index) => allHits.findIndex(({ handle }) => handle === hit.handle) === index,
              )
              .sort((hitA, hitB) => {
                // eslint-disable-next-line
                if (hitA._score < hitB._score) return 1;
                // eslint-disable-next-line
                if (hitA._score > hitB._score) return -1;
                return 0;
              })
              .slice(0, limit)
          : allHits;

      if (hits.length === 0) return Promise.resolve([]);

      if (!compilationTemplate) {
        return Promise.resolve(hits);
      }

      return fetchRecommendationsHTML(hits);
    } catch {
      return Promise.resolve([]);
    }
  };

  return { query };
};

export default createAlgoliaRecommendationsEngine;
